import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../utils/api';
import config from '../config/config';
import './PackageImpactGraph.css';

const BASE_URL = config.BASE_URL;

const SEV_COLOR = {
  CRITICAL: '#dc2626',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#16a34a',
  '': '#6366f1',
};

const GRADE_COLOR = {
  A: '#16a34a',
  B: '#84cc16',
  C: '#d97706',
  D: '#ea580c',
  F: '#dc2626',
};

function sevLabel(s) {
  if (!s) return 'None';
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function RiskBadge({ severity }) {
  const sev = (severity || 'none').toLowerCase();
  return <span className={`ce-sev ce-sev--${sev}`}>{severity || 'None'}</span>;
}

function GradeBadge({ grade }) {
  return (
    <span className="ce-tag" style={{ color: GRADE_COLOR[grade] || 'var(--bs-secondary-color)', fontWeight: 700 }}>
      Grade {grade || '—'}
    </span>
  );
}

export default function PackageImpactGraph() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Search / autocomplete state
  const [query, setQuery] = useState(searchParams.get('name') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const searchInputRef = useRef(null);

  // Selected package to display
  const [selectedPkg, setSelectedPkg] = useState(
    searchParams.get('name')
      ? {
          name: searchParams.get('name'),
          purl: searchParams.get('purl') || '',
          version: searchParams.get('version') || '',
        }
      : null
  );

  // Impact data
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Graph interaction
  const fgRef = useRef();
  const containerRef = useRef();
  // Initialize with window size (sidebar ~220px, topbar 48px) so first render fills space
  const [dimensions, setDimensions] = useState({
    width: Math.max(600, window.innerWidth - 240),
    height: Math.max(400, window.innerHeight - 48 - 160), // subtract header+search+stats
  });
  const [selectedNode, setSelectedNode] = useState(null);
  const selectedNodeRef = useRef(null);
  const [hoveredNode, setHoveredNode] = useState(null);

  // View mode: 'graph' | 'table'
  const [viewMode, setViewMode] = useState('graph');
  // Table sort
  const [sortCol, setSortCol] = useState('worstSeverity');
  const [sortDir, setSortDir] = useState('asc');

  // Filters
  const [filterSev, setFilterSev] = useState('ALL');
  const [filterPolicy, setFilterPolicy] = useState('ALL');
  const [filterGrade, setFilterGrade] = useState('ALL');
  const [filterSearch, setFilterSearch] = useState('');

  // Pulse animation
  const pulseRef = useRef(0);

  // ── Animation loop ──────────────────────────────────────────────────────────
  // react-force-graph-2d v1.29 has no refresh() — drive pulse via setInterval
  // updating pulseRef so nodeCanvasObject always draws the latest phase.
  useEffect(() => {
    const id = setInterval(() => {
      pulseRef.current = Date.now();
    }, 16); // ~60 fps
    return () => clearInterval(id);
  }, []);

  // ── Resize observer ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: Math.max(400, entry.contentRect.height),
        });
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  // ── Autocomplete search ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await authFetch(
          `${BASE_URL}/packages/search?q=${encodeURIComponent(query.trim())}`
        );
        if (!res.ok) return;
        const data = await res.json();
        setSuggestions(Array.isArray(data) ? data.slice(0, 10) : []);
        setShowSuggestions(true);
      } catch {
        /* silent */
      }
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // ── Load impact data ────────────────────────────────────────────────────────
  const loadImpact = useCallback(async (pkg) => {
    if (!pkg) return;
    setLoading(true);
    setError(null);
    setImpact(null);
    setSelectedNode(null);
    selectedNodeRef.current = null;
    try {
      const params = new URLSearchParams();
      if (pkg.name) params.set('name', pkg.name);
      if (pkg.purl) params.set('purl', pkg.purl);
      if (pkg.version) params.set('version', pkg.version);
      const res = await authFetch(`${BASE_URL}/packages/impact?${params}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setImpact(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load if URL params present
  useEffect(() => {
    if (selectedPkg) loadImpact(selectedPkg);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectPackage = (pkg) => {
    const sp = { name: pkg.name, purl: pkg.purl || '', version: '' };
    setSelectedPkg(sp);
    setQuery(pkg.name);
    setShowSuggestions(false);
    setSearchParams(
      Object.fromEntries(
        Object.entries(sp).filter(([, v]) => v)
      )
    );
    loadImpact(sp);
  };

  // ── Filtering ───────────────────────────────────────────────────────────────
  const filteredRepos = useMemo(() => {
    if (!impact?.repos) return [];
    return impact.repos.filter((r) => {
      if (filterSev !== 'ALL') {
        if (filterSev === 'NONE' && r.vulnCount > 0) return false;
        if (filterSev !== 'NONE' && r.worstSeverity !== filterSev) return false;
      }
      if (filterPolicy !== 'ALL' && r.policyStatus !== filterPolicy) return false;
      if (filterGrade !== 'ALL' && r.qualityGrade !== filterGrade) return false;
      if (
        filterSearch.trim() &&
        !r.repoName.toLowerCase().includes(filterSearch.toLowerCase())
      )
        return false;
      return true;
    });
  }, [impact, filterSev, filterPolicy, filterGrade, filterSearch]);

  // ── Table sort ──────────────────────────────────────────────────────────────
  const SEV_ORDER = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3, '': 4 };
  const sortedRepos = useMemo(() => {
    const arr = [...filteredRepos];
    arr.sort((a, b) => {
      let av, bv;
      switch (sortCol) {
        case 'worstSeverity':
          av = SEV_ORDER[a.worstSeverity] ?? 4;
          bv = SEV_ORDER[b.worstSeverity] ?? 4;
          break;
        case 'vulnCount':    av = a.vulnCount;           bv = b.vulnCount;           break;
        case 'portfolioRiskScore': av = a.portfolioRiskScore; bv = b.portfolioRiskScore; break;
        case 'qualityGrade': av = a.qualityGrade || 'Z'; bv = b.qualityGrade || 'Z'; break;
        case 'craScore':     av = a.craScore;            bv = b.craScore;            break;
        case 'policyStatus': av = a.policyStatus || '';  bv = b.policyStatus || '';  break;
        default:             av = a.repoName;            bv = b.repoName;
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRepos, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIndicator = ({ col }) => {
    if (sortCol !== col) return <span style={{ opacity: 0.3 }}>⇅</span>;
    return sortDir === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  // ── Graph data ──────────────────────────────────────────────────────────────
  const graphData = useMemo(() => {
    if (!impact) return null;
    const worstOverall =
      impact.withCritical > 0
        ? 'CRITICAL'
        : impact.withHigh > 0
        ? 'HIGH'
        : '';

    const pkgNode = {
      id: '__pkg__',
      type: 'package',
      name: impact.name,
      version: impact.version,
      purl: impact.purl,
      worstSeverity: worstOverall,
      totalPackages: 0,
      fx: 0,
      fy: 0,
    };

    const n = filteredRepos.length;
    // Pre-compute radial positions so nodes start spread out.
    // Radius scales with node count to prevent overlap.
    const radius = Math.max(220, n * 38);
    const repoNodes = filteredRepos.map((r, i) => {
      const angle = (2 * Math.PI * i) / Math.max(1, n) - Math.PI / 2;
      return {
      id: r.repoName,
      type: 'repo',
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle),
      repoName: r.repoName,
      worstSeverity: r.worstSeverity || '',
      vulnCount: r.vulnCount,
      cveIds: r.cveIds,
      totalVulns: r.totalVulns,
      criticalVulns: r.criticalVulns,
      highVulns: r.highVulns,
      qualityScore: r.qualityScore,
      qualityGrade: r.qualityGrade,
      portfolioRiskScore: r.portfolioRiskScore,
      abandonedCount: r.abandonedCount,
      malwareCount: r.malwareCount,
      policyStatus: r.policyStatus,
      craScore: r.craScore,
      supplierTrustScore: r.supplierTrustScore,
      supplierTrustTier: r.supplierTrustTier,
      totalPackages: r.totalPackages,
      packageVersion: r.packageVersion,
      lastScanned: r.lastScanned,
    };
    });

    const links = repoNodes.map((n) => ({
      source: '__pkg__',
      target: n.id,
      vulnCount: n.vulnCount,
      severity: n.worstSeverity,
    }));

    return { nodes: [pkgNode, ...repoNodes], links };
  }, [impact, filteredRepos]);

  // ── Configure D3 forces after graphData is ready ────────────────────────────
  useEffect(() => {
    if (!fgRef.current || !graphData) return;
    fgRef.current.d3Force('charge').strength(-600).distanceMax(800);
    const n = Math.max(1, graphData.nodes.length - 1);
    const radius = Math.max(220, n * 38);
    fgRef.current.d3Force('link').distance(radius).strength(0.15);
    fgRef.current.d3Force('center')?.strength(0.05);
    fgRef.current.d3ReheatSimulation();
  }, [graphData]);

  // ── Canvas node renderer ────────────────────────────────────────────────────
  const nodeCanvasObject = useCallback(
    (node, ctx, globalScale) => {
      const isPkg = node.type === 'package';
      const r = isPkg
        ? 22
        : Math.max(8, Math.min(18, 8 + (node.totalPackages || 0) / 50));
      const color =
        SEV_COLOR[node.worstSeverity] || (isPkg ? '#6366f1' : SEV_COLOR['']);
      const isSelected = selectedNodeRef.current?.id === node.id;
      const isHovered = hoveredNode?.id === node.id;

      // Pulsing ring for package node or CRITICAL repos
      if (isPkg || node.worstSeverity === 'CRITICAL') {
        const t = pulseRef.current / 1000;
        const speed = isPkg ? 1.5 : 2.5;
        const ring = r + 5 + Math.sin(t * speed) * 4;
        const alpha = 0.35 + 0.25 * Math.sin(t * speed);
        ctx.beginPath();
        ctx.arc(node.x, node.y, ring, 0, 2 * Math.PI);
        ctx.strokeStyle = color;
        ctx.lineWidth = isPkg ? 2.5 : 2;
        ctx.globalAlpha = alpha;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Glow for selected / hovered
      if (isSelected || isHovered) {
        ctx.shadowBlur = 24;
        ctx.shadowColor = color;
      }

      // Main fill
      ctx.beginPath();
      ctx.arc(node.x, node.y, r, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();

      // Specular highlight
      ctx.beginPath();
      ctx.arc(node.x, node.y - r * 0.32, r * 0.42, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(255,255,255,0.16)';
      ctx.fill();

      // Selected ring
      if (isSelected) {
        ctx.beginPath();
        ctx.arc(node.x, node.y, r + 4, 0, 2 * Math.PI);
        ctx.strokeStyle = '#1e1b4b';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      ctx.shadowBlur = 0;

      // Label
      const showLabel = isPkg || globalScale >= 1.1 || isSelected || isHovered;
      if (showLabel) {
        const raw = isPkg ? node.name : node.repoName;
        const label = raw && raw.length > 20 ? raw.slice(0, 18) + '…' : raw || '';
        const fontSize = isPkg ? 12 : 9;
        ctx.font = `${isPkg ? 600 : 400} ${fontSize}px Inter, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';

        // Label: dark text on semi-transparent white pill for light background
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.beginPath();
        const lx = node.x - tw / 2 - 4;
        const ly = node.y + r + 4;
        const lw = tw + 8;
        const lh = fontSize + 5;
        const lr = 3;
        ctx.moveTo(lx + lr, ly);
        ctx.lineTo(lx + lw - lr, ly);
        ctx.arcTo(lx + lw, ly, lx + lw, ly + lh, lr);
        ctx.lineTo(lx + lw, ly + lh - lr);
        ctx.arcTo(lx + lw, ly + lh, lx + lw - lr, ly + lh, lr);
        ctx.lineTo(lx + lr, ly + lh);
        ctx.arcTo(lx, ly + lh, lx, ly + lh - lr, lr);
        ctx.lineTo(lx, ly + lr);
        ctx.arcTo(lx, ly, lx + lr, ly, lr);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = isPkg ? '#1e1b4b' : '#1e293b';
        ctx.fillText(label, node.x, node.y + r + 6);
        ctx.textBaseline = 'alphabetic';
      }
    },
    [hoveredNode]
  );

  // ── Custom link renderer ────────────────────────────────────────────────────
  const linkCanvasObject = useCallback((link, ctx) => {
    const src = link.source;
    const tgt = link.target;
    if (
      !src ||
      !tgt ||
      src.x == null ||
      tgt.x == null
    )
      return;
    const color = SEV_COLOR[tgt.worstSeverity] || SEV_COLOR[''];
    const width = Math.max(1, Math.log((link.vulnCount || 0) + 1) * 1.5);
    ctx.beginPath();
    ctx.moveTo(src.x, src.y);
    ctx.lineTo(tgt.x, tgt.y);
    ctx.strokeStyle = color;
    ctx.lineWidth = width;
    ctx.globalAlpha = 0.4;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }, []);

  // ── CSV export ──────────────────────────────────────────────────────────────
  const exportCSV = () => {
    if (!impact?.repos) return;
    const header = [
      'repo',
      'package',
      'version',
      'worstSeverity',
      'vulnCount',
      'cveIds',
      'qualityGrade',
      'riskScore',
      'policyStatus',
      'craScore',
      'supplierTrust',
    ].join(',');
    const rows = impact.repos.map((r) =>
      [
        r.repoName,
        impact.name,
        r.packageVersion,
        r.worstSeverity || 'none',
        r.vulnCount,
        `"${(r.cveIds || []).join(' ')}"`,
        r.qualityGrade || '',
        r.portfolioRiskScore.toFixed(1),
        r.policyStatus || '',
        r.craScore,
        r.supplierTrustTier || '',
      ].join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `impact-${impact.name || 'package'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── KPI counts ──────────────────────────────────────────────────────────────
  const kpiTotal = impact?.totalAffected ?? 0;
  const kpiCritical = impact?.withCritical ?? 0;
  const kpiHigh = impact?.withHigh ?? 0;
  const kpiClean = impact ? kpiTotal - (impact.withAnyVuln ?? 0) : 0;

  // ── Keyboard: close panel ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        setSelectedNode(null);
        selectedNodeRef.current = null;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="pig-page">

      {/* ── Header: standard page style ── */}
      <div className="pig-topstrip">
        <div className="pig-topstrip-main">
          <div className="pig-topstrip-title">
            <h1 className="ce-page-title">🌐 {t('impact.title', 'Cross-Project Impact Graph')}</h1>
            <p className="ce-page-subtitle">
              {t('impact.subtitle', 'See which projects are exposed when a package is vulnerable')}
            </p>
          </div>

          {/* Search bar */}
          <div className="pig-search-wrap">
            <div className="pig-search-bar">
              <span className="pig-search-icon">🔎</span>
              <input
                ref={searchInputRef}
                className="pig-search-input"
                type="text"
                placeholder={t('impact.searchPlaceholder', 'Search for a package (e.g. lodash, log4j)…')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowSuggestions(true);
            }}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 180)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                selectPackage({ name: query.trim(), purl: '' });
              }
            }}
            autoFocus
          />
          {loading && <span className="pig-search-spinner" />}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <ul className="pig-autocomplete">
                {suggestions.map((s, i) => (
                  <li key={i} className="pig-autocomplete-item" onMouseDown={() => selectPackage(s)}>
                    <span className="pig-ac-name">{s.name}</span>
                    {s.ecosystem && <span className="ce-tag">{s.ecosystem}</span>}
                    <span className="pig-ac-repos">
                      {s.repoCount} {s.repoCount === 1 ? 'project' : 'projects'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {impact && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              {/* View mode toggle */}
              <div className="pig-view-toggle">
                <button
                  className={`ce-btn ce-btn--sm ${viewMode === 'graph' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                  onClick={() => setViewMode('graph')}
                  title="Graph view"
                >🌐 {t('impact.viewGraph', 'Graph')}</button>
                <button
                  className={`ce-btn ce-btn--sm ${viewMode === 'table' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                  onClick={() => setViewMode('table')}
                  title="Table view"
                >📋 {t('impact.viewTable', 'Table')}</button>
              </div>
              <button className="ce-btn ce-btn--primary ce-btn--sm" onClick={exportCSV}>
                ⬇ {t('impact.exportCsv', 'Export CSV')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="pig-error">
          <span>⚠️</span> {error}
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading && (
        <div className="pig-skeleton-wrap">
          <div className="pig-skeleton pig-skeleton--stats" />
          <div className="pig-skeleton pig-skeleton--graph" />
        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !impact && !error && (
        <div className="empty-state" style={{ paddingTop: '5rem' }}>
          <span className="empty-icon">🌐</span>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.5rem', color: 'var(--bs-body-color)' }}>
            {t('impact.emptyTitle', 'Explore Package Impact')}
          </h2>
          <p style={{ maxWidth: 480, margin: '0 auto 1.5rem', fontSize: '0.875rem', color: 'var(--bs-secondary-color)' }}>
            {t('impact.emptyDesc', 'Search for a package above to see which projects across your portfolio are affected — including their vulnerability exposure, quality grades, and risk scores.')}
          </p>
          <div className="pig-empty-steps">
            <div className="pig-empty-step"><span className="pig-empty-step-num">1</span><span>{t('impact.step1', 'Type a package name in the search box')}</span></div>
            <div className="pig-empty-step"><span className="pig-empty-step-num">2</span><span>{t('impact.step2', 'Select from suggestions or press Enter')}</span></div>
            <div className="pig-empty-step"><span className="pig-empty-step-num">3</span><span>{t('impact.step3', 'Click nodes in the graph for details')}</span></div>
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {!loading && impact && (
        <>
          {/* KPI cards */}
          <div className="pig-kpi-area">
            <div className="ce-kpi-row" style={{ marginBottom: '0.75rem' }}>
              <div className="ce-kpi" style={{ '--kpi-color': 'var(--accent)' }}>
                <div className="ce-kpi-value">{kpiTotal}</div>
                <div className="ce-kpi-label">{t('impact.kpiTotal', 'Affected Projects')}</div>
              </div>
              <div className="ce-kpi" style={{ '--kpi-color': '#dc2626' }}>
                <div className="ce-kpi-value">{kpiCritical}</div>
                <div className="ce-kpi-label">{t('impact.kpiCritical', 'With CRITICAL CVE')}</div>
              </div>
              <div className="ce-kpi" style={{ '--kpi-color': '#ea580c' }}>
                <div className="ce-kpi-value">{kpiHigh}</div>
                <div className="ce-kpi-label">{t('impact.kpiHigh', 'With HIGH CVE')}</div>
              </div>
              <div className="ce-kpi" style={{ '--kpi-color': '#16a34a' }}>
                <div className="ce-kpi-value">{kpiClean}</div>
                <div className="ce-kpi-label">{t('impact.kpiClean', 'No CVE Issues')}</div>
              </div>
            </div>

          {/* Filter bar */}
          <div className="pig-filter-bar">
            <div className="pig-filter-group">
              <span className="pig-filter-label">{t('impact.filterSev', 'Severity')}:</span>
              {['ALL', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE'].map((s) => (
                <button
                  key={s}
                  className={`ce-btn ce-btn--sm ${filterSev === s ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                  onClick={() => setFilterSev(s)}
                >
                  {s === 'NONE' ? 'Clean' : s}
                </button>
              ))}
            </div>
            <div className="pig-filter-group">
              <span className="pig-filter-label">{t('impact.filterPolicy', 'Policy')}:</span>
              {['ALL', 'PASS', 'FAIL'].map((s) => (
                <button
                  key={s}
                  className={`ce-btn ce-btn--sm ${filterPolicy === s ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                  onClick={() => setFilterPolicy(s)}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="pig-filter-group">
              <span className="pig-filter-label">{t('impact.filterGrade', 'Grade')}:</span>
              {['ALL', 'A', 'B', 'C', 'D', 'F'].map((g) => (
                <button
                  key={g}
                  className={`ce-btn ce-btn--sm ${filterGrade === g ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                  onClick={() => setFilterGrade(g)}
                >
                  {g}
                </button>
              ))}
            </div>
            <input
              className="pig-filter-search"
              placeholder={t('impact.filterRepo', 'Filter repos…')}
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
            />
            <span className="ce-filter-count">
              {filteredRepos.length} / {(impact.repos || []).length} projects
            </span>
          </div>
          </div>

          {/* Graph + side panel */}
          {viewMode === 'graph' && (
          <div className="pig-body">
            <div className="ce-card pig-canvas-wrap" ref={containerRef}>
              {graphData && (
                <ForceGraph2D
                  ref={fgRef}
                  graphData={graphData}
                  width={dimensions.width}
                  height={dimensions.height}
                  backgroundColor="rgba(0,0,0,0)"
                  nodeCanvasObject={nodeCanvasObject}
                  nodeCanvasObjectMode={() => 'replace'}
                  linkCanvasObject={linkCanvasObject}
                  linkCanvasObjectMode={() => 'replace'}
                  nodeVal={(n) =>
                    n.type === 'package'
                      ? 20
                      : Math.max(4, (n.vulnCount || 0) * 2 + 4)
                  }
                  onNodeClick={(node) => {
                    if (node.type === 'repo') {
                      setSelectedNode(node);
                      selectedNodeRef.current = node;
                    } else {
                      setSelectedNode(null);
                      selectedNodeRef.current = null;
                    }
                  }}
                  onNodeHover={(node) => {
                    setHoveredNode(node || null);
                    document.body.style.cursor = node ? 'pointer' : 'default';
                  }}
                  onBackgroundClick={() => {
                    setSelectedNode(null);
                    selectedNodeRef.current = null;
                  }}
                  cooldownTicks={Infinity}
                  d3AlphaDecay={0.02}
                  d3VelocityDecay={0.4}
                  enableNodeDrag={true}
                  enableZoomInteraction={true}
                />
              )}

              {/* Legend */}
              <div className="pig-legend">
                {Object.entries(SEV_COLOR).map(([sev, color]) => (
                  <div key={sev} className="pig-legend-item">
                    <span
                      className="pig-legend-dot"
                      style={{ background: color }}
                    />
                    <span className="pig-legend-label">
                      {sev === '' ? 'No CVE' : sev}
                    </span>
                  </div>
                ))}
                <div className="pig-legend-item">
                  <span
                    className="pig-legend-dot pig-legend-dot--pkg"
                    style={{ background: '#6366f1' }}
                  />
                  <span className="pig-legend-label">Package (center)</span>
                </div>
              </div>
            </div>

            {/* Side panel — ce-card */}
            {selectedNode && (
              <div className="ce-card pig-side-panel">
                <div className="ce-card-header" style={{ justifyContent: 'space-between' }}>
                  <span>📦 {selectedNode.repoName}</span>
                  <button
                    className="ce-icon-btn"
                    style={{ width: 24, height: 24, fontSize: '0.75rem' }}
                    onClick={() => { setSelectedNode(null); selectedNodeRef.current = null; }}
                    title="Close"
                  >✕</button>
                </div>

                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.875rem' }}>
                  <RiskBadge severity={selectedNode.worstSeverity} />
                  <GradeBadge grade={selectedNode.qualityGrade} />
                  {selectedNode.policyStatus && (
                    <span className={`ce-pill ${selectedNode.policyStatus === 'PASS' ? 'ce-pill-success' : 'ce-pill-danger'}`}>
                      {selectedNode.policyStatus}
                    </span>
                  )}
                </div>

                <div className="pig-side-section-title">{t('impact.packageExposure', 'Package Exposure')}</div>
                <div className="ce-info-grid" style={{ marginBottom: '1rem' }}>
                  <span className="ce-info-label">{t('impact.version', 'Version')}</span>
                  <span className="ce-info-value ce-mono">{selectedNode.packageVersion || '—'}</span>
                  <span className="ce-info-label">{t('impact.pkgCves', 'CVEs from this pkg')}</span>
                  <span className="ce-info-value">{selectedNode.vulnCount}</span>
                  <span className="ce-info-label">{t('impact.totalVulns', 'Total repo vulns')}</span>
                  <span className="ce-info-value">{selectedNode.totalVulns}</span>
                  <span className="ce-info-label">{t('impact.criticalVulns', 'Critical')}</span>
                  <span className="ce-info-value" style={{ color: '#dc2626', fontWeight: 700 }}>{selectedNode.criticalVulns}</span>
                </div>

                <div className="pig-side-section-title">{t('impact.projectHealth', 'Project Health')}</div>
                <div className="ce-info-grid" style={{ marginBottom: '1rem' }}>
                  <span className="ce-info-label">{t('impact.riskScore', 'Risk Score')}</span>
                  <span className="ce-info-value">{selectedNode.portfolioRiskScore?.toFixed(1) ?? '—'}</span>
                  <span className="ce-info-label">{t('impact.craScore', 'CRA Score')}</span>
                  <span className="ce-info-value">{selectedNode.craScore ?? '—'}</span>
                  <span className="ce-info-label">{t('impact.supplierTrust', 'Supplier Trust')}</span>
                  <span className="ce-info-value">{selectedNode.supplierTrustTier || '—'}{selectedNode.supplierTrustScore ? ` (${selectedNode.supplierTrustScore})` : ''}</span>
                  <span className="ce-info-label">{t('impact.abandoned', 'Abandoned pkgs')}</span>
                  <span className="ce-info-value" style={selectedNode.abandonedCount > 0 ? { color: '#ea580c', fontWeight: 700 } : {}}>{selectedNode.abandonedCount}</span>
                  <span className="ce-info-label">{t('impact.malware', 'Malware flags')}</span>
                  <span className="ce-info-value" style={selectedNode.malwareCount > 0 ? { color: '#dc2626', fontWeight: 700 } : {}}>{selectedNode.malwareCount}</span>
                  <span className="ce-info-label">{t('impact.totalPackages', 'Total packages')}</span>
                  <span className="ce-info-value">{selectedNode.totalPackages}</span>
                </div>

                {selectedNode.cveIds && selectedNode.cveIds.length > 0 && (
                  <>
                    <div className="pig-side-section-title">{t('impact.cveIds', 'CVE IDs')}</div>
                    <div className="pig-cve-list">
                      {selectedNode.cveIds.slice(0, 8).map((id) => (
                        <span
                          key={id}
                          className="ce-tag ce-mono pig-cve-link"
                          style={{ fontSize: '0.65rem', cursor: 'pointer' }}
                          title={`Open ${id} in ${selectedNode.repoName}`}
                          onClick={() => navigate(`/RepoDetails/${encodeURIComponent(selectedNode.repoName)}`)}
                        >{id}</span>
                      ))}
                      {selectedNode.cveIds.length > 8 && (
                        <span className="ce-tag" style={{ color: 'var(--accent-text)' }}>+{selectedNode.cveIds.length - 8} more</span>
                      )}
                    </div>
                  </>
                )}

                <button
                  className="ce-btn ce-btn--primary"
                  style={{ width: '100%', marginTop: 'auto', justifyContent: 'center' }}
                  onClick={() => navigate(`/RepoDetails/${encodeURIComponent(selectedNode.repoName)}`)}
                >
                  {t('impact.openProject', 'Open Project')} →
                </button>
              </div>
            )}
          </div>
          )} {/* end graph view */}

          {/* Table view */}
          {viewMode === 'table' && (
            <div className="ce-card pig-table-wrap">
              <table className="pig-table">
                <thead>
                  <tr>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('repoName')}>
                      Project <SortIndicator col="repoName" />
                    </th>
                    <th className="pig-th">Version</th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('worstSeverity')}>
                      Severity <SortIndicator col="worstSeverity" />
                    </th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('vulnCount')}>
                      CVEs <SortIndicator col="vulnCount" />
                    </th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('qualityGrade')}>
                      Grade <SortIndicator col="qualityGrade" />
                    </th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('portfolioRiskScore')}>
                      Risk Score <SortIndicator col="portfolioRiskScore" />
                    </th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('policyStatus')}>
                      Policy <SortIndicator col="policyStatus" />
                    </th>
                    <th className="pig-th pig-th--sortable" onClick={() => handleSort('craScore')}>
                      CRA <SortIndicator col="craScore" />
                    </th>
                    <th className="pig-th">CVE IDs</th>
                    <th className="pig-th"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRepos.map((r) => (
                    <tr key={r.repoName} className="pig-tr">
                      <td className="pig-td pig-td--name">{r.repoName}</td>
                      <td className="pig-td ce-mono" style={{ fontSize: '0.75rem' }}>{r.packageVersion || '—'}</td>
                      <td className="pig-td">
                        {r.worstSeverity
                          ? <span className={`ce-sev ce-sev--${r.worstSeverity.toLowerCase()}`}>{r.worstSeverity}</span>
                          : <span style={{ color: 'var(--bs-secondary-color)' }}>None</span>}
                      </td>
                      <td className="pig-td" style={{ fontWeight: r.vulnCount > 0 ? 700 : 400, color: r.vulnCount > 0 ? SEV_COLOR[r.worstSeverity] || 'inherit' : 'inherit' }}>
                        {r.vulnCount}
                      </td>
                      <td className="pig-td" style={{ color: GRADE_COLOR[r.qualityGrade] || 'var(--bs-secondary-color)', fontWeight: 700 }}>
                        {r.qualityGrade || '—'}
                      </td>
                      <td className="pig-td">{r.portfolioRiskScore?.toFixed(1) ?? '—'}</td>
                      <td className="pig-td">
                        {r.policyStatus
                          ? <span className={`ce-pill ${r.policyStatus === 'PASS' ? 'ce-pill-success' : 'ce-pill-danger'}`}>{r.policyStatus}</span>
                          : '—'}
                      </td>
                      <td className="pig-td">{r.craScore ?? '—'}</td>
                      <td className="pig-td pig-td--cves">
                        {(r.cveIds || []).slice(0, 3).map((id) => (
                          <span
                            key={id}
                            className="ce-tag ce-mono pig-cve-link"
                            style={{ fontSize: '0.65rem', cursor: 'pointer' }}
                            title={`View ${id} in ${r.repoName}`}
                            onClick={() => navigate(`/RepoDetails/${encodeURIComponent(r.repoName)}`)}
                          >{id}</span>
                        ))}
                        {(r.cveIds || []).length > 3 && (
                          <span className="ce-tag" style={{ color: 'var(--accent-text)', fontSize: '0.65rem' }}>
                            +{r.cveIds.length - 3}
                          </span>
                        )}
                      </td>
                      <td className="pig-td">
                        <button
                          className="ce-btn ce-btn--secondary ce-btn--sm"
                          onClick={() => navigate(`/RepoDetails/${encodeURIComponent(r.repoName)}`)}
                        >Open →</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {sortedRepos.length === 0 && (
                <div className="pig-filter-empty">
                  <span>🔍</span> {t('impact.filterEmpty', 'No projects match the current filters.')}
                </div>
              )}
            </div>
          )}

          {(impact.repos || []).length === 0 && (
            <div className="pig-filter-empty">
              <span>✅</span> {t('impact.noRepos', 'No projects in your portfolio use this package.')}
            </div>
          )}
        </>
      )}
    </div>
  );
}
